import styled from 'styled-components';

import { GetSupplyOrdersQuery, SupplyOrderState } from 'data/__generated__';

import { colors } from 'constants/colors';
import { renderAsPrice } from '../../../helpers/price';
import { SUPPLY_ORDER_STATES_LABELS, getColorsFromState } from '../constants/states';
import { dateFromString } from '../../../helpers/dateTimes';

type SupplyOrderCalendarEventProps = {
    supplyOrder: GetSupplyOrdersQuery['supplyOrders'][number];
    isSelected: boolean;
};

export const SupplyOrderCalendarEvent = ({ supplyOrder, isSelected }: SupplyOrderCalendarEventProps) => {
    const { state, supplier, number, priceHT, isAtSoldOutRisk, isMerged, dateDeliveryScheduled, weeklyMenusStartDate } =
        supplyOrder;

    const { backgroundColor, labelColor } = getColorsFromState(state, weeklyMenusStartDate, isMerged);

    const today = new Date();
    const dateDeliveryScheduledDateObject = dateDeliveryScheduled ? dateFromString(dateDeliveryScheduled) : null;
    const isDateDeliveryScheduledToday =
        dateDeliveryScheduledDateObject?.getFullYear() === today.getFullYear() &&
        dateDeliveryScheduledDateObject?.getMonth() === today.getMonth() &&
        dateDeliveryScheduledDateObject?.getDate() === today.getDate();
    const isDateDeliveryScheduledInPast =
        !!dateDeliveryScheduledDateObject && !isDateDeliveryScheduledToday && dateDeliveryScheduledDateObject < today;
    const isWaitingForDelivery = [SupplyOrderState.Ordered, SupplyOrderState.Confirmed].includes(state);

    return (
        <EventContainer
            $backgroundColor={backgroundColor}
            $labelColor={labelColor}
            $isSelected={isSelected}
            $isAtRisk={!!isAtSoldOutRisk}
            $isLate={isWaitingForDelivery && isDateDeliveryScheduledInPast}
            $isToday={isWaitingForDelivery && isDateDeliveryScheduledToday}
        >
            <EventTitle>{supplier.name ?? 'Pas de fournisseur'}</EventTitle>
            <EventDetails>
                <span>#{number || '?'}</span>
                {priceHT !== null ? <span>{renderAsPrice(priceHT)}</span> : null}
                <EventState>{SUPPLY_ORDER_STATES_LABELS[state]}</EventState>
            </EventDetails>
        </EventContainer>
    );
};

const EventContainer = styled.div<{
    $backgroundColor: string;
    $labelColor: string;
    $isSelected: boolean;
    $isAtRisk: boolean;
    $isLate: boolean;
    $isToday: boolean;
}>`
    display: flex;
    flex-direction: column;
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 11px;
    line-height: 1.3;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    color: ${({ $labelColor }) => $labelColor};
    border-left: ${({ $isAtRisk, $isLate, $isToday }) =>
        $isAtRisk
            ? `3px solid ${colors.pink}`
            : $isLate
              ? `3px solid ${colors.red}`
              : $isToday
                ? `3px solid ${colors.orange}`
                : '3px solid transparent'};
    opacity: ${({ $isSelected }) => ($isSelected ? 1 : 0.6)};
    outline: ${({ $isSelected }) => ($isSelected ? '3px solid #000' : 'none')};
    outline-offset: -1px;
    box-shadow: ${({ $isSelected }) =>
        $isSelected ? '0 0 0 2px white, 0 0 0 5px #000, 0 4px 12px rgba(0,0,0,0.3)' : 'none'};
    transform: ${({ $isSelected }) => ($isSelected ? 'scale(1.04)' : 'none')};
    z-index: ${({ $isSelected }) => ($isSelected ? 10 : 'auto')};
    cursor: pointer;
    overflow: hidden;
    transition: all 0.15s ease;

    &:hover {
        opacity: 1;
    }
`;

const EventTitle = styled.div`
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const EventDetails = styled.div`
    display: flex;
    gap: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const EventState = styled.span`
    font-style: italic;
`;
